<template>
  <v-container fluid class="content-container">
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="6">
        <h2>Parrains</h2>
      </v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-btn icon @click="load"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-col>
    </v-row>

    <v-card class="data-card">
      <v-card-title class="card-header">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Rechercher..."
          single-line
          hide-details
          outlined
          dense
          clearable
          class="table-search"
        ></v-text-field>

        <div class="status-filters">
          <v-chip
            small
            :outlined="statusFilter !== 'ALL'"
            :color="statusFilter === 'ALL' ? 'primary' : 'grey'"
            class="filter-chip"
            @click="setStatusFilter('ALL')"
          >
            Tous
          </v-chip>
          <v-chip
            small
            :outlined="statusFilter !== 'ACTIVE'"
            color="success"
            :class="['filter-chip', statusFilter === 'ACTIVE' ? 'white--text' : '']"
            @click="setStatusFilter('ACTIVE')"
          >
            <v-icon left x-small>mdi-check-circle</v-icon>
            Actifs
          </v-chip>
          <v-chip
            small
            :outlined="statusFilter !== 'BLOCKED'"
            color="error"
            :class="['filter-chip', statusFilter === 'BLOCKED' ? 'white--text' : '']"
            @click="setStatusFilter('BLOCKED')"
          >
            <v-icon left x-small>mdi-cancel</v-icon>
            Bloqués
          </v-chip>
        </div>

        <v-spacer></v-spacer>
        <v-chip class="results-chip ml-3" small>
          {{ filteredReferrers.length }} parrains
        </v-chip>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredReferrers"
        :loading="loading"
        :items-per-page="25"
        loading-text="Chargement..."
        no-data-text="Aucun parrain"
        class="modern-table"
      >
        <template v-slot:[`item.name`]="{ item }">
          <strong>{{ item.first_name }} {{ item.last_name }}</strong>
          <div class="grey--text">{{ item.phone }}</div>
        </template>

        <template v-slot:[`item.payout`]="{ item }">
          <v-chip x-small :color="payoutColor(item.payout_method)" text-color="white">
            {{ item.payout_method }}
          </v-chip>
        </template>

        <template v-slot:[`item.total_paid`]="{ item }">
          {{ formatAmount(item.total_paid) }}
        </template>

        <template v-slot:[`item.total_due`]="{ item }">
          {{ formatAmount(item.total_due) }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip x-small :color="statusColor(item.status)" text-color="white">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn small color="primary" dark @click="openDetail(item)">
            <v-icon left small>mdi-eye</v-icon>
            VOIR
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Fiche parrain -->
    <v-dialog v-model="detailDialog" max-width="1200" :fullscreen="$vuetify.breakpoint.smAndDown">
      <v-card v-if="detailDialog">
        <v-toolbar color="primary" dark dense flat>
          <v-toolbar-title>
            {{ detail ? `${detail.first_name} ${detail.last_name} — Fiche parrain` : 'Fiche parrain' }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-row v-if="detailLoading" justify="center" class="my-6">
            <v-progress-circular indeterminate color="primary" />
          </v-row>

          <v-row v-else-if="detail">
            <!-- Profil -->
            <v-col cols="12" md="4">
              <v-card outlined>
                <v-card-title class="subtitle-1">Profil</v-card-title>
                <v-card-text>
                  <div class="mb-2">
                    <v-icon small class="mr-1">mdi-phone</v-icon>{{ detail.phone }}
                  </div>
                  <div class="mb-2" v-if="detail.email">
                    <v-icon small class="mr-1">mdi-email</v-icon>{{ detail.email }}
                  </div>

                  <v-chip small :color="payoutColor(detail.payout_method)" text-color="white" class="mb-2">
                    {{ detail.payout_method }}
                  </v-chip>

                  <div v-for="row in payoutDetailsRows(detail)" :key="row.label" class="grey--text caption">
                    {{ row.label }} : {{ row.value }}
                  </div>

                  <v-divider class="my-3" />

                  <v-chip small :color="statusColor(detail.status)" text-color="white" class="mb-3">
                    {{ statusLabel(detail.status) }}
                  </v-chip>

                  <div>
                    <v-btn
                      small outlined color="primary" class="mr-2 mb-2"
                      @click="openResetPin"
                    >
                      RESET PIN
                    </v-btn>
                    <v-btn
                      small dark class="mb-2"
                      :color="detail.status === 'BLOCKED' ? 'success' : 'error'"
                      :loading="blocking"
                      @click="toggleBlock"
                    >
                      {{ detail.status === 'BLOCKED' ? 'DÉBLOQUER' : 'BLOQUER' }}
                    </v-btn>
                  </div>

                  <v-divider class="my-3" />

                  <div class="subtitle-2 font-weight-bold mb-2">Associer un business</div>
                  <v-autocomplete
                    v-model="selectedBusinessId"
                    :items="availableBusinesses"
                    item-text="name"
                    item-value="id"
                    label="Rechercher un business…"
                    dense outlined hide-details clearable
                    class="mb-2"
                  >
                    <template v-slot:item="{ item }">
                      {{ item.name }} <span class="grey--text ml-1">({{ item.phone }})</span>
                    </template>
                  </v-autocomplete>
                  <v-btn
                    color="primary" block
                    :disabled="!selectedBusinessId"
                    :loading="associating"
                    @click="associateBusiness"
                  >
                    ASSOCIER
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Paiements + Businesses -->
            <v-col cols="12" md="8">
              <v-tabs v-model="detailTab" class="mb-3">
                <v-tab>Paiements</v-tab>
                <v-tab>Businesses associés</v-tab>
                <v-tab>Demandes</v-tab>
              </v-tabs>

              <div v-if="detailTab === 0">
                <v-chip outlined color="primary" class="mr-2 mb-2">
                  Payé : {{ formatAmount(detail.total_paid) }} HTG
                </v-chip>
                <v-chip outlined color="primary" class="mb-2">
                  Dû : {{ formatAmount(detail.total_due) }} HTG
                </v-chip>

                <v-data-table
                  :headers="rewardHeaders"
                  :items="detail.rewards || []"
                  :items-per-page="10"
                  dense
                  no-data-text="Aucune récompense"
                  class="elevation-0"
                >
                  <template v-slot:[`item.amount`]="{ item }">
                    {{ formatAmount(item.amount) }} {{ item.currency }}
                  </template>

                  <template v-slot:[`item.business`]="{ item }">
                    {{ item.business ? item.business.name : '—' }}
                  </template>

                  <template v-slot:[`item.created_at`]="{ item }">
                    {{ formatDate(item.created_at) }}
                  </template>

                  <template v-slot:[`item.payment_reference`]="{ item }">
                    <span v-if="item.payment_reference">{{ item.payment_reference }}</span>
                    <span v-else class="grey--text">—</span>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <v-chip
                      x-small
                      :color="rewardStatusColor(item.status)"
                      :text-color="item.status === 'DUE' ? 'black' : 'white'"
                    >
                      {{ rewardStatusLabel(item.status) }}
                    </v-chip>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      v-if="item.status === 'DUE'"
                      x-small color="success" dark
                      @click="openPay(item)"
                    >
                      PAYER
                    </v-btn>
                  </template>
                </v-data-table>
              </div>

              <div v-else-if="detailTab === 1">
                <v-simple-table dense v-if="associatedBusinesses.length">
                  <template v-slot:default>
                    <tbody>
                      <tr v-for="biz in associatedBusinesses" :key="biz.id">
                        <td>{{ biz.name }}</td>
                        <td class="grey--text">{{ biz.phone }}</td>
                        <td>
                          <v-chip
                            x-small
                            :color="biz.rewardStatus ? rewardStatusColor(biz.rewardStatus) : 'grey'"
                            :text-color="biz.rewardStatus === 'DUE' ? 'black' : 'white'"
                          >
                            {{ biz.rewardStatus ? rewardStatusLabel(biz.rewardStatus) : 'AUCUNE RÉCOMPENSE' }}
                          </v-chip>
                        </td>
                        <td class="text-right">
                          <v-btn
                            x-small text color="error"
                            :loading="dissociatingId === biz.id"
                            @click="dissociateBusiness(biz)"
                          >
                            DISSOCIER
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div v-else class="grey--text">Aucun business associé</div>
              </div>

              <div v-else>
                <v-data-table
                  :headers="leadHeaders"
                  :items="detail.leads || []"
                  :items-per-page="10"
                  dense
                  no-data-text="Aucune demande d'association"
                  class="elevation-0"
                >
                  <template v-slot:[`item.phones`]="{ item }">
                    <div>{{ item.phone1 }}</div>
                    <div v-if="item.phone2" class="grey--text caption">{{ item.phone2 }}</div>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <v-chip x-small :color="leadStatusColor(item.status)" text-color="white">
                      {{ leadStatusLabel(item.status) }}
                    </v-chip>
                  </template>

                  <template v-slot:[`item.created_at`]="{ item }">
                    {{ formatDate(item.created_at) }}
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Reset PIN -->
    <v-dialog v-model="resetPinDialog" max-width="420">
      <v-card>
        <v-card-title>Réinitialiser le PIN</v-card-title>
        <v-card-text>
          <v-form ref="pinForm">
            <v-text-field
              v-model="pin"
              label="Nouveau PIN *"
              type="password"
              maxlength="4"
              :rules="pinRules"
              outlined dense
            />
            <v-text-field
              v-model="pinConfirm"
              label="Confirmation *"
              type="password"
              maxlength="4"
              :rules="pinConfirmRules"
              outlined dense
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="resetPinDialog = false">Annuler</v-btn>
          <v-btn color="primary" :loading="resettingPin" @click="confirmResetPin">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payer une récompense -->
    <v-dialog v-model="payDialog" max-width="480">
      <v-card v-if="selectedReward">
        <v-card-title>Marquer la récompense payée</v-card-title>
        <v-card-text>
          <p>
            {{ formatAmount(selectedReward.amount) }} {{ selectedReward.currency }}
            <span v-if="selectedReward.business"> — {{ selectedReward.business.name }}</span><br>
            <span v-if="detail">{{ detail.payout_method }} : {{ payoutSummary(detail) }}</span>
          </p>
          <v-form ref="payForm">
            <v-text-field
              v-model="payReference"
              label="Référence du paiement *"
              :rules="[v => !!v || 'La référence est requise']"
              outlined dense
            />
            <v-textarea v-model="payNote" label="Note (optionnel)" outlined dense rows="2" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="payDialog = false">Annuler</v-btn>
          <v-btn color="success" :loading="paying" @click="confirmPay">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment'
import numeral from 'numeral'

export default {
  name: 'ReferrersView',
  data: () => ({
    search: '',
    statusFilter: 'ALL',
    loading: false,
    referrers: [],
    searchTimeout: null,

    detailDialog: false,
    detailLoading: false,
    detail: null,
    detailTab: 0,

    resetPinDialog: false,
    pin: '',
    pinConfirm: '',
    resettingPin: false,

    blocking: false,

    allBusinesses: [],
    selectedBusinessId: null,
    associating: false,
    dissociatingId: null,

    payDialog: false,
    selectedReward: null,
    payReference: '',
    payNote: '',
    paying: false,

    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',

    headers: [
      { text: 'Parrain', value: 'name', sortable: false },
      { text: 'Payout', value: 'payout', sortable: false },
      { text: 'Leads', value: 'leads_count' },
      { text: 'Payé', value: 'total_paid' },
      { text: 'Dû', value: 'total_due' },
      { text: 'Statut', value: 'status' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
    rewardHeaders: [
      { text: 'Montant', value: 'amount' },
      { text: 'Business', value: 'business', sortable: false },
      { text: 'Créée le', value: 'created_at' },
      { text: 'Référence', value: 'payment_reference', sortable: false },
      { text: 'Statut', value: 'status' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
    leadHeaders: [
      { text: 'Business', value: 'business_name' },
      { text: 'Propriétaire', value: 'owner_name' },
      { text: 'Téléphones', value: 'phones', sortable: false },
      { text: 'Statut', value: 'status' },
      { text: 'Date', value: 'created_at' },
    ],
  }),
  computed: {
    filteredReferrers() {
      if (this.statusFilter === 'ALL') return this.referrers
      return this.referrers.filter(r => r.status === this.statusFilter)
    },
    pinRules() {
      return [
        v => !!v || 'Le PIN est requis',
        v => /^\d{4}$/.test(v) || 'Le PIN doit comporter 4 chiffres',
      ]
    },
    pinConfirmRules() {
      return [
        v => !!v || 'La confirmation est requise',
        v => v === this.pin || 'Les PIN ne correspondent pas',
      ]
    },
    associatedBusinesses() {
      if (!this.detail || !this.detail.businesses) return []
      const rewardStatusByBusiness = {}
      ;(this.detail.rewards || []).forEach(r => {
        if (r.business) rewardStatusByBusiness[r.business.id] = r.status
      })
      return this.detail.businesses.map(b => ({
        ...b,
        rewardStatus: rewardStatusByBusiness[b.id] || null,
      }))
    },
    availableBusinesses() {
      return this.allBusinesses.filter(b => !b.referrer_id)
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.load(), 300)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    setStatusFilter(value) {
      this.statusFilter = value
    },
    async load() {
      this.loading = true
      try {
        const res = await this.$axios.get('/referrers', {
          params: { search: this.search || undefined },
        })
        this.referrers = res.data.data
      } catch (e) {
        this.notify('Erreur lors du chargement des parrains', 'error')
      } finally {
        this.loading = false
      }
    },
    async openDetail(item) {
      this.detail = null
      this.selectedBusinessId = null
      this.detailTab = 0
      this.detailDialog = true
      await Promise.all([this.loadDetail(item.id), this.loadBusinesses()])
    },
    async loadDetail(id) {
      this.detailLoading = true
      try {
        const res = await this.$axios.get(`/referrers/${id}`)
        this.detail = res.data.data
      } catch (e) {
        this.notify('Erreur lors du chargement du parrain', 'error')
      } finally {
        this.detailLoading = false
      }
    },
    async loadBusinesses() {
      try {
        const res = await this.$axios.get('/businesses?orderasc=name')
        this.allBusinesses = res.data
      } catch (e) {
        this.notify('Erreur lors du chargement des businesses', 'error')
      }
    },
    async associateBusiness() {
      if (!this.selectedBusinessId || !this.detail) return
      this.associating = true
      try {
        await this.$axios.post(`/businesses/${this.selectedBusinessId}/referrer`, {
          code: this.detail.code,
        })
        this.selectedBusinessId = null
        this.notify('Business associé', 'success')
        await Promise.all([this.loadDetail(this.detail.id), this.loadBusinesses()])
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || "échec de l'association"), 'error')
      } finally {
        this.associating = false
      }
    },
    async dissociateBusiness(biz) {
      if (!this.detail) return
      this.dissociatingId = biz.id
      try {
        await this.$axios.delete(`/businesses/${biz.id}/referrer`)
        this.notify('Business dissocié', 'success')
        await Promise.all([this.loadDetail(this.detail.id), this.loadBusinesses()])
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec de la dissociation'), 'error')
      } finally {
        this.dissociatingId = null
      }
    },
    openResetPin() {
      this.pin = ''
      this.pinConfirm = ''
      this.resetPinDialog = true
      this.$nextTick(() => { this.$refs.pinForm && this.$refs.pinForm.resetValidation() })
    },
    async confirmResetPin() {
      if (!this.$refs.pinForm.validate()) return
      this.resettingPin = true
      try {
        const payload = {
          first_name: this.detail.first_name,
          last_name: this.detail.last_name,
          phone: this.detail.phone,
          email: this.detail.email,
          payout_method: this.detail.payout_method,
          payout_details: this.detail.payout_details,
          pin: this.pin,
        }
        await this.$axios.put(`/referrers/${this.detail.id}`, payload)
        this.resetPinDialog = false
        this.notify('PIN réinitialisé', 'success')
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec de la réinitialisation'), 'error')
      } finally {
        this.resettingPin = false
      }
    },
    async toggleBlock() {
      if (!this.detail) return
      this.blocking = true
      const action = this.detail.status === 'BLOCKED' ? 'unblock' : 'block'
      try {
        const res = await this.$axios.post(`/referrers/${this.detail.id}/${action}`)
        this.detail.status = res.data.data.status
        const row = this.referrers.find(r => r.id === this.detail.id)
        if (row) row.status = this.detail.status
        this.notify(action === 'block' ? 'Parrain bloqué' : 'Parrain débloqué', 'success')
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || "échec de l'opération"), 'error')
      } finally {
        this.blocking = false
      }
    },
    openPay(reward) {
      this.selectedReward = reward
      this.payReference = ''
      this.payNote = ''
      this.payDialog = true
      this.$nextTick(() => { this.$refs.payForm && this.$refs.payForm.resetValidation() })
    },
    async confirmPay() {
      if (!this.$refs.payForm.validate()) return
      this.paying = true
      try {
        await this.$axios.post(`/referral-rewards/${this.selectedReward.id}/pay`, {
          payment_reference: this.payReference,
          note: this.payNote || undefined,
        })
        this.payDialog = false
        this.notify('Récompense marquée payée', 'success')
        await this.loadDetail(this.detail.id)
        await this.load()
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec du paiement'), 'error')
      } finally {
        this.paying = false
      }
    },
    notify(text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
    payoutDetailsRows(referrer) {
      const d = referrer.payout_details || {}
      const rows = []
      if (d.account_name) rows.push({ label: 'Titulaire', value: d.account_name })
      if (d.wallet_phone) rows.push({ label: 'Téléphone wallet', value: d.wallet_phone })
      if (d.bank_name) rows.push({ label: 'Banque', value: d.bank_name })
      if (d.account_type) rows.push({ label: 'Type de compte', value: d.account_type })
      if (d.account_number) rows.push({ label: 'N° de compte', value: d.account_number })
      if (d.currency) rows.push({ label: 'Devise', value: d.currency })
      return rows
    },
    payoutSummary(referrer) {
      const d = referrer.payout_details || {}
      if (referrer.payout_method === 'BANK') {
        const account = `${d.account_number || ''}${d.currency ? ' ' + d.currency : ''}`
        return `${d.bank_name || ''} ${d.account_type || ''} ${account} (${d.account_name || ''})`
      }
      return `${d.wallet_phone || ''} (${d.account_name || ''})`
    },
    payoutColor(method) {
      return { MONCASH: 'red', NATCASH: 'blue', BANK: 'teal' }[method] || 'grey'
    },
    statusColor(status) {
      return status === 'BLOCKED' ? 'error' : 'success'
    },
    statusLabel(status) {
      return status === 'BLOCKED' ? 'BLOQUÉ' : 'ACTIF'
    },
    rewardStatusColor(status) {
      return { DUE: 'amber', PAID: 'success', CANCELLED: 'grey' }[status] || 'grey'
    },
    rewardStatusLabel(status) {
      return { DUE: 'DUE', PAID: 'PAYÉE', CANCELLED: 'ANNULÉE' }[status] || status
    },
    leadStatusColor(status) {
      return { NOUVEAU: 'blue-grey', CONTACTE: 'info', CONVERTI: 'success', REJETE: 'error' }[status] || 'grey'
    },
    leadStatusLabel(status) {
      return { NOUVEAU: 'NOUVEAU', CONTACTE: 'CONTACTÉ', CONVERTI: 'CONVERTI', REJETE: 'REJETÉ' }[status] || status
    },
    formatAmount(v) {
      return numeral(Number(v) || 0).format('0,0')
    },
    formatDate(v) {
      return moment(v).format('DD/MM/YYYY HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.content-container {
  padding: 0 24px 24px;
}

.data-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.card-header {
  padding: 16px 20px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.theme--dark .card-header {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.table-search {
  max-width: 300px;

  ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 8px !important;
  }
}

.status-filters {
  display: flex;
  gap: 6px;
}

.filter-chip {
  cursor: pointer;
  font-weight: 500 !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

.results-chip {
  font-weight: 500;
}

.modern-table {
  ::v-deep th {
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }

  ::v-deep td {
    padding: 16px !important;
  }

  ::v-deep tbody tr:hover {
    background: rgba(0, 63, 116, 0.04);
  }
}

.theme--dark .modern-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.04);
}
</style>
